import React, { useMemo } from "react";
import { size, truncate } from "lodash";
import styled from "styled-components";
import { FontSize, FontWeight, Spacing, content, ellipsis } from "@glossy";
import { dominant } from "@glossy/theme";
import { Locale } from "@linkfluence/models/locale";
import { Platform, getPlatformColor } from "@linkfluence/models/platform";
import { parse as parseEmojis } from "@tootools/emoji";
import { parseStatus } from "@tootools/social";
import SafeHTML from "@ui-kit/safe-html";
import { Post, PostLensFactory } from "@radarly/models/posts";
import PhotoMentions from "./photo-mentions";

const Root = styled.div<{ $smallFont: boolean; $small: boolean }>`
  font-size: ${({ $smallFont }) =>
    $smallFont ? FontSize.Medium : FontSize.Large};
  line-height: initial;
  height: ${({ $small }) => $small && "20px"};
  word-break: break-word;
  color: ${content};

  a {
    color: ${dominant};
  }
`;

const Title = styled.div<{ $small: boolean }>`
  ${({ $small }) => $small && ellipsis()};
  font-size: ${({ $small }) => ($small ? FontSize.Medium : FontSize.Large)};
  margin-bottom: ${Spacing.ExtraSmall};
  font-weight: ${({ $small }) =>
    $small ? FontWeight.Normal : FontWeight.Bold};
  color: ${content};
`;

const Text = styled.div<{ $color: string; $small: boolean }>`
  ${({ $small }) => $small && ellipsis()};

  hl {
    font-weight: ${FontWeight.Bold};
  }

  a {
    color: ${({ $color }) => $color};
  }
`;

interface IProps {
  post: Post;
  limit: number;
  title?: string;
  className?: string;
  parse?: boolean;
  small?: boolean;
  locale: Locale;
}

const parseContent = (
  content: string,
  platform: Platform,
  parse: boolean,
  limit?: number
) =>
  parse
    ? parseStatus(content, platform, limit)
    : parseEmojis(
        limit && limit > 0 ? truncate(content, { length: limit }) : content
      );

const RadarlyPostContent = ({
  post,
  className,
  limit,
  parse,
  small,
  locale,
}: IProps) => {
  const lens = PostLensFactory.create(post);
  const platform = lens.getPlatform();
  const text = lens.getText();
  const title = lens.getTitle();
  const photoMentions = lens.getPhotoMentions();
  const parsedText = useMemo(() => {
    return parseContent(text, platform, parse, limit);
  }, [text, platform, parse, limit]);
  const parsedTitle = useMemo(() => {
    return parseContent(title, platform, parse, 140);
  }, [parse, platform, title]);
  const smallFont = !!(lens.hasTitle() || lens.getLargePreviewData() || small);

  return (
    <Root $smallFont={smallFont} $small={small} className={className}>
      {size(parsedTitle) > 0 ? (
        <SafeHTML as={Title} $small={small} html={parsedTitle} />
      ) : (
        <SafeHTML
          as={Text}
          $color={getPlatformColor(platform)}
          $small={small}
          html={parsedText}
          options={{
            ALLOWED_TAGS: ["b", "i", "em", "strong", "a", "hl", "br", "img"],
            ALLOWED_ATTR: ["href", "class", "target", "src", "alt"],
          }}
        />
      )}

      {photoMentions && (
        <PhotoMentions photoMentions={photoMentions} locale={locale} />
      )}
    </Root>
  );
};

export default RadarlyPostContent;
